<template>
  <main class="main main--has-floating-buttons">
    <portal to="headerTitle">
      {{ $t('admin.pageHotel.title') }}
    </portal>
    <template v-if="!isNil(hotel)">
      <card title="Images" cols="12 6@desktop">
        <hm-card-inner :title="$t('admin.pageHotel.card.logo')" >
          <hm-input-file v-model="hotel.logo" :url="serviceUrls.hotelDetails.uploadFile(this, 'logo')" :label="$t('admin.pageHotel.logo')" cols="12" />
          <hm-col v-if="hotel.logo.fileURL" cols="12" class="hm-input__image-wrapper">
            <img :src="hotel.logo.fileURL" class="hm-input__image">
          </hm-col>
          <hm-input-file v-model="hotel.icon" :url="serviceUrls.hotelDetails.uploadFile(this, 'icon')" :label="$t('admin.pageHotel.icon')" cols="12" />
          <hm-col v-if="hotel.icon.fileURL" cols="12" class="hm-input__image-wrapper">
            <img :src="hotel.icon.fileURL" class="hm-input__image">
          </hm-col>
        </hm-card-inner>
        <hm-card-inner title="Images">
          <multi-image-uploader v-model="hotel.imageIds" :parent-id="hotelSettings.hotel.id" :disabled="hotel.deactivated" :label="$t('admin.pageRoomType.imageIds')" />
        </hm-card-inner>
      </card>
      <card :rows="1" :title="$t('admin.pageHotel.card.contactDetails')" cols="12 6@desktop start-7@desktop">
        <hm-card-inner>
          <hm-input :lazy="true" v-model="hotel.reservationPhoneNumber" :label="$t('admin.pageHotel.reservationPhoneNumber')" cols="12" />
          <hm-input :lazy="true" v-model="hotel.emailReservation" :label="$t('admin.pageHotel.emailReservation')" cols="12" />
          <hm-input :lazy="true" v-model="hotel.emailTechnical" :label="$t('admin.pageHotel.emailTechnical')" cols="12" />
          <hm-input :lazy="true" v-model="hotel.urlWebsite" :label="$t('admin.pageHotel.urlWebsite')" cols="12" />
        </hm-card-inner>
        <hm-card-inner :title="$t('admin.pageHotel.card.verifiedEmailSender')">
          <hm-col v-if="hotel.emailSenderVerified === true && hotel.additionalFields[`AWS_SES_DOMAIN_VERIFIED_${(hotel.additionalFields.AWS_SES_DOMAIN || '').toUpperCase()}`] !== true" cols="12">
            <span class="warning-text col-12">{{ $t('admin.pageHotel.infoVerifiedEmailAddress') }}</span>
          </hm-col>
          <hm-input :disabled="!hasRole('ROLE_SYSADMIN')"
                    :lazy="true"
                    v-model="hotel.emailSender"
                    :label="$t('admin.pageHotel.emailSender')"
                    :has-warning="!isEmpty(hotel.emailSender) && !hotel.emailSenderVerified"
                    :help="isEmpty(hotel.emailSender) || hotel.emailSenderVerified ? null : $t('admin.pageHotel.emailSender_notVerified')"
                    validate="email"
                    cols="12">
            <template slot="icon-inline-right">
              <input-icon-button v-if="hotel.emailSenderVerified"
                                 :title="$t('admin.pageHotel.verifiedEmailSender')"
                                 :border="false"
                                 :disabled="true"
                                 icon="save"/>
              <input-icon-button v-else-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                                 :title="`${$t('admin.pageHotel.verifyEmailSender')} 🔒`"
                                 :border="true"
                                 icon="overbooking"
                                 @click="verifyEmailSender(hotel.emailSender)"/>
              <input-icon-button v-if="!hasRole('ROLE_SYSADMIN') && hasRole('ROLE_ADMIN')"
                                 :title="$t('admin.pageHotel.clearEmailSender')"
                                 :border="true"
                                 icon="delete"
                                 @click="hotel.emailSender = null"/>
            </template>
          </hm-input>
          <hm-input :lazy="true"
                    v-model="hotel.emailSenderName"
                    :label="$t('admin.pageHotel.emailSenderName')"
                    cols="12" />
          <span v-if="!isEmpty(hotel.emailSender) && hotel.emailSenderSpfValidated === false" class="warning-text col-12 cursor--copy" @click="$copy(striptags($t('admin.pageHotel.hintSpfInvalid')))" v-html="$t('admin.pageHotel.hintSpfInvalid')"></span>
        </hm-card-inner>
        <hm-card-inner v-if="!isEmpty(hotel.additionalFields.AWS_SES_DOMAIN) || hasRole('ROLE_SYSADMIN')"
                       :title="$t('admin.pageHotel.card.verifiedEmailDomain')">
          <hm-input :disabled="!hasRole('ROLE_SYSADMIN')"
                    :lazy="true"
                    v-model="hotel.additionalFields.AWS_SES_DOMAIN"
                    :label="$t('admin.pageHotel.emailSenderDomain')"
                    :has-warning="!isEmpty(hotel.additionalFields.AWS_SES_DOMAIN) && !hotel.additionalFields[`AWS_SES_DOMAIN_VERIFIED_${(hotel.additionalFields.AWS_SES_DOMAIN || '').toUpperCase()}`]"
                    :help="isEmpty(hotel.additionalFields.AWS_SES_DOMAIN) || hotel.additionalFields[`AWS_SES_DOMAIN_VERIFIED_${(hotel.additionalFields.AWS_SES_DOMAIN || '').toUpperCase()}`] ? null : $t('admin.pageHotel.emailSenderDomain_notVerified')"
                    cols="12">
            <template slot="icon-inline-right">
              <input-icon-button v-if="hotel.additionalFields[`AWS_SES_DOMAIN_VERIFIED_${(hotel.additionalFields.AWS_SES_DOMAIN || '').toUpperCase()}`]"
                                 :title="`${$t('admin.pageHotel.verifiedEmailSender')} 🔒`"
                                 :border="false"
                                 :disabled="!hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                                 icon="save"
                                 @click="verifyEmailDomain(hotel.additionalFields.AWS_SES_DOMAIN)"/>
              <input-icon-button v-else
                                 :title="$t('admin.pageHotel.verifyEmailDomain')"
                                 :border="true"
                                 icon="overbooking"
                                 @click="verifyEmailDomain(hotel.additionalFields.AWS_SES_DOMAIN)" />
            </template>
          </hm-input>
        </hm-card-inner>
        <template v-if="!isEmpty(hotel.additionalFields.AWS_SES_DOMAIN) && !isEmpty(hotel.additionalFields.AWS_SES_DOMAIN_KEY)">
          <hm-card-inner ref="dkimData"
                         :title="`${$t('admin.pageHotel.card.dkimRecords')} (${hotel.additionalFields.AWS_SES_DOMAIN})`"
                         cols="12"
                         style="cursor: copy"
                         full-size>
            <hm-col v-if="!isEmpty(hotel.additionalFields.AWS_SES_DOMAIN) && hotel.additionalFields[`AWS_SES_DOMAIN_VERIFIED_${(hotel.additionalFields.AWS_SES_DOMAIN || '').toUpperCase()}`] !== true" cols="12">
              <span v-if="['Hotelmeister'].includes(hotelSettings.hotel.whiteLabel)" class="info-text info-text--full-size info-text--no-margin col-12">{{ $t('admin.pageHotel.infoDkimRecordsHotelmeister') }}</span>
              <span v-else class="info-text info-text--full-size info-text--no-margin col-12">{{ $t('admin.pageHotel.infoDkimRecords') }}</span>
            </hm-col>
            <hm-col cols="12">
              <p style="font-family: monospace,monospace;font-size: 1.4rem;padding: 0 1.5rem;" @click="copyDkimData">
                <template v-for="(value, key) in hotel.additionalFields" v-if="!isEmpty(key) && key.indexOf(`AWS_SES_DKIM_${(hotel.additionalFields.AWS_SES_DOMAIN || '?').toUpperCase()}`) === 0">
                  {{ value }}._domainkey IN CNAME {{ value }}.dkim.amazonses.com.<br/><br/>
                </template>
              </p>
            </hm-col>
          </hm-card-inner>
          <hm-card-inner ref="spf" full-size style="cursor: copy">
            <hm-col cols="12" style="padding: 0 1.5rem">
              <p @click="copyDkimData">{{ $t('admin.pageHotel.infoSpfRecord') }}</p>
              <pre @click="copyDkimData">include:_spf.hotelsoftware.io</pre>
            </hm-col>
          </hm-card-inner>
          <hm-card-inner ref="dmarc" full-size style="cursor: copy">
            <hm-col cols="12" style="padding: 0 1.5rem">
              <p @click="copyDkimData">{{ $t('admin.pageHotel.infoDmarcRecord') }}</p>
            </hm-col>
          </hm-card-inner>
        </template>
        <template v-if="!isEmpty(hotel.additionalFields.AWS_SES_MAIL_FROM_DOMAIN) || hasRole('ROLE_SYSADMIN')">
          <hm-card-inner v-if="!isEmpty(hotel.additionalFields.AWS_SES_DOMAIN)" :title="$t('admin.pageHotel.card.mailFromDomain')">
            <hm-input :value="hotel.additionalFields.AWS_SES_MAIL_FROM_DOMAIN || `hotelsoftware.${hotel.additionalFields.AWS_SES_DOMAIN}`"
                      :label="$t('admin.pageHotel.mailFromDomain')"
                      :help="$t('admin.pageHotel.infoMailFromDomain')"
                      :key="hotel.additionalFields.AWS_SES_MAIL_FROM_DOMAIN"
                      disabled
                      cols="12">
              <template slot="icon-inline-right">
                <input-icon-button v-if="hotel.additionalFields['AWS_SES_MAIL_FROM_DOMAIN_VERIFIED']"
                                   :title="$t('admin.pageHotel.mailFromDomainVerified')"
                                   :border="false"
                                   disabled
                                   icon="save"/>
                <input-icon-button v-else
                                   :title="$t('admin.pageHotel.verifyMailFromDomain')"
                                   :border="true"
                                   icon="overbooking"
                                   @click="verifyEmailFromDomain()"/>
                <input-icon-button v-if="!isEmpty(hotel.additionalFields.AWS_SES_MAIL_FROM_DOMAIN)"
                                   :title="$t('admin.pageHotel.deleteMailFromDomain')"
                                   :border="true"
                                   icon="delete"
                                   @click="removeEmailFromDomain()"/>
              </template>
            </hm-input>
          </hm-card-inner>
          <hm-card-inner v-if="!isEmpty(hotel.additionalFields.AWS_SES_MAIL_FROM_DOMAIN) && !isEmpty(hotel.additionalFields.AWS_SES_DOMAIN)"
                         ref="mailFromDomainData"
                         :title="`${$t('admin.pageHotel.card.mailFromRecords')} (${hotel.additionalFields.AWS_SES_DOMAIN})`"
                         full-size
                         style="cursor: copy">
            <hm-col cols="12">
              <p style="font-family: monospace,monospace;font-size: 1.4rem;padding: 0 1.5rem;" @click="copyDkimData">
                {{ hotel.additionalFields.AWS_SES_MAIL_FROM_DOMAIN.split('.')[0] }} IN MX 10 feedback-smtp.eu-west-1.amazonses.com.<br/><br/>
                {{ hotel.additionalFields.AWS_SES_MAIL_FROM_DOMAIN.split('.')[0] }} IN TXT "v=spf1 include:amazonses.com ~all"
              </p>
            </hm-col>
          </hm-card-inner>
          <hm-card-inner v-if="hasLicense('Inquiry') && hotel.additionalFields" title="reCAPTCHA v3">
            <hm-input v-model="hotel.additionalFields.RECAPTCHA_SECRET_KEY"
                      type="password"
                      :label="$t('admin.pageHotelSettings.RECAPTCHA_SECRET_KEY')"/>
          </hm-card-inner>
        </template>
      </card>
      <card :title="$t('admin.pageHotel.card.locationSettings')" cols="12 6@desktop start-7@desktop">
        <hm-card-inner title="IDs">
          <hm-input :value="$root.hotel" :disabled="true" label="Hotel ID">
            <template slot="icon-inline-right" slot-scope="slot">
              <input-icon-button :border="true" :title="$t('admin.pageRoom.copyToClipboard')" icon="copy" @click="$copy($root.hotel)" />
            </template>
          </hm-input>
        </hm-card-inner>
        <hm-card-inner v-if="hotelSettings.hotel.whiteLabel === 'Hotelmeister' && hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN') && hotel.additionalFields" title="Hotelmeister Billing 🔒">
          <hm-input v-model="hotel.additionalFields.SEVDESK_CUSTOMER_NUMBER"
                    :label="$t('admin.pageHotelSettings.SEVDESK_CUSTOMER_NUMBER')"/>
          <hm-input v-model="hotel.additionalFields.SEVDESK_CONTACT_ID"
                    :disabled="!hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                    :label="$t('admin.pageHotelSettings.SEVDESK_CONTACT_ID')"/>
        </hm-card-inner>
        <hm-card-inner v-if="hasRole('ROLE_SYSADMIN')" title="White-Label Billing">
          <hm-input :lazy="true" v-model="hotel.externalAccountId" label="External Account ID" help="Required for billing (White Label Partners only)" />
          <hm-input-select v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                           id="whiteLabelPartner"
                           v-model="hotel.whiteLabelPartner"
                           label="White-Label Partner 🔒"
                           name="whiteLabelPartner"
                           enum-name="WhiteLabelPartner"/>
        </hm-card-inner>
        <hm-card-inner v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')" label="Partner Admins 🔒">
          <template v-for="partnerTenant in hotelSettings.partnerAdmins">
            <hm-input-toggle :key="partnerTenant.tenantId" :value="hotel.partnerAdminRefs.map(it => it.tenantId).includes(partnerTenant.tenantId)" :label="partnerTenant.name" @input="onPartnerAdminStatusChange(partnerTenant.tenantId, $event)"/>
          </template>
        </hm-card-inner>
        <hm-card-inner :title="$t('admin.pageHotel.currency')">
          <hm-input :lazy="true" v-model="hotel.currency" :disabled="!hasRole('ROLE_SYSTEM')" :label="$t('admin.pageHotel.currency')" />
        </hm-card-inner>
        <hm-card-inner :title="$t('admin.pageHotel.card.language')">
          <hm-input-select id="timeZone"
                           v-model="hotel.timeZone"
                           :disabled="!hasRole('ROLE_SYSADMIN')"
                           :clearable="false"
                           :label="$t('admin.pageHotel.timeZone')"
                           name="timeZone">
            <option v-for="item in zonesForCountry(hotel.countryCode)" :value="item" v-html="item"></option>
          </hm-input-select>
          <hm-select id="primaryLanguage"
                     v-model="hotel.primaryLanguage"
                     :options="getI18nLanguages($shortLocale)"
                     :disabled="!hasRole('ROLE_SYSADMIN')"
                     :clearable="false"
                     :label="$t('admin.pageHotel.primaryLanguage')"
                     name="primaryLanguage" />
          <hm-select id="fallbackLanguage"
                     v-model="hotel.fallbackLanguage"
                     :options="fallbackLanguages"
                     :clearable="false"
                     :label="$t('admin.pageHotel.fallbackLanguage')"
                     :help="$t('admin.pageHotel.fallbackLanguage_help')"
                     name="fallbackLanguage" />
          <hm-multiselect id="additionalLanguages"
                          v-model="hotel.additionalLanguages"
                          :options="additionalLanguages"
                          :label="$t('admin.pageHotel.additionalLanguages')"
                          name="additionalLanguages"
                          label-expression="${option.name}" />
        </hm-card-inner>
        <hm-card-inner :title="$t('admin.pageHotel.card.gpsLocation')">
          <hm-input-number :lazy="true"
                           v-model="hotel.latitude"
                           :min-fraction-digist="2"
                           :max-fraction-digits="8"
                           :label="$t('admin.pageHotel.latitude')"
                           :help="$t('admin.pageHotel.latitude_help')"
                           locale="en"
                           cols="12" />
          <hm-input-number :lazy="true"
                           v-model="hotel.longitude"
                           :min-fraction-digist="2"
                           :max-fraction-digits="8"
                           :label="$t('admin.pageHotel.longitude')"
                           :help="$t('admin.pageHotel.longitude_help')"
                           locale="en"
                           cols="12" />
          <hm-input :lazy="true"
                    v-model="hotel.gpsAddress"
                    :label="$t('admin.pageHotel.gpsAddress')"
                    :help="$t('admin.pageHotel.gpsAddress_help')"
                    cols="12" />
        </hm-card-inner>
      </card>
      <card :title="$t('admin.pageHotel.card.bankDetails')" cols="12 6@desktop start-7@desktop">
        <hm-input :lazy="true" v-model="hotel.bankName" :label="$t('admin.pageHotel.bankName')" cols="12" />
        <hm-input :lazy="true" v-model="hotel.iban" :label="$t('admin.pageHotel.iban')" cols="12" />
        <hm-input :lazy="true" v-model="hotel.bic" :label="$t('admin.pageHotel.bic')" cols="12" />
      </card>
      <card :title="$t('admin.pageHotel.card.address')" cols="12 6@desktop start-1" rows="2 start-2">
        <hm-card-inner :title="$t('admin.pageHotel.card.hotelAddress')">
          <hm-input :lazy="true" v-model="hotel.name" :label="$t('admin.pageHotel.name')" cols="12" />
          <hm-textarea :lazy="true"
                       v-model="hotel.hotelStreetAddress"
                       :input-rows="2"
                       :label="$t('admin.pageHotel.address')"
                       name="address" />
          <hm-input-zip-city :zip="hotel.hotelZipCode"
                             :city="hotel.hotelCity"
                             :country="hotel.countryCode"
                             :label-zip="$t('admin.pageHotel.hotelZipCode')"
                             :label-city="$t('admin.pageHotel.hotelCity')"
                             @update:zip="hotel.hotelZipCode = $event"
                             @update:city="hotel.hotelCity = $event" />
          <hm-input :lazy="true"
                    v-model="hotel.hotelStateProvince"
                    :label="$t('admin.pageHotel.hotelStateProvince')"
                    :help="$t('admin.pageHotel.hotelStateProvince_help')"
                    cols="12" />
          <hm-select id="country"
                     v-model="hotel.countryCode"
                     :options="getI18nCountries($shortLocale)"
                     :disabled="true"
                     :clearable="false"
                     :label="$t('admin.pageHotel.country')"
                     name="country"
                     cols="12" />
        </hm-card-inner>
        <hm-card-inner :title="$t('admin.pageHotel.card.companyAddress')">
          <hm-input :lazy="true" v-model="hotel.companyName" :label="$t('admin.pageHotel.companyName')" cols="12" />
          <hm-textarea :lazy="true"
                       v-model="hotel.streetAddress"
                       :input-rows="2"
                       :label="$t('admin.pageHotel.address')"
                       name="address" />
          <hm-input-zip-city :zip="hotel.zipCode"
                             :city="hotel.city"
                             :country="hotel.countryCode"
                             :label-zip="$t('admin.pageHotel.zipCode')"
                             :label-city="$t('admin.pageHotel.city')"
                             @update:zip="hotel.zipCode = $event"
                             @update:city="hotel.city = $event" />
          <hm-input :lazy="true"
                    v-model="hotel.stateProvince"
                    :label="$t('admin.pageHotel.stateProvince')"
                    :help="$t('admin.pageHotel.stateProvince_help')"
                    cols="12" />
          <hm-select id="country"
                     v-model="hotel.countryCode"
                     :options="getI18nCountries($shortLocale)"
                     :disabled="true"
                     :clearable="false"
                     :label="$t('admin.pageHotel.country')"
                     name="country"
                     cols="12" />
          <hm-input :lazy="true" v-model="hotel.uidNumber" :label="$t('admin.pageHotel.uidNumber')" cols="12" />
          <hm-input :lazy="true" v-model="hotel.firmenbuchnummer" :label="$t('admin.pageHotel.firmenbuchnummer')" cols="12" />
          <hm-input :lazy="true" v-model="hotel.firmenbuchgericht" :label="$t('admin.pageHotel.firmenbuchgericht')" cols="12" />
        </hm-card-inner>
      </card>
      <card v-if="hasRole('ROLE_SYSADMIN')" :title="$t('admin.pageHotel.card.licenses')" cols="12 start-1@desktop">
        <hm-multiselect v-if="!isEmpty(featureBundles) && !isEmpty(featureBundlesOptions) && !isNil(disabledFeatureBundles)"
                        v-model="hotel.featureBundles"
                        :options="featureBundlesOptions"
                        :disabled-options="disabledFeatureBundles"
                        :label="$t('admin.pageHotel.licenseBundles')"
                        :limit="10"
                        name="featureBundles"
                        label-expression="${option.name}" />
        <hm-multiselect v-if="!isEmpty(addOnLicenses)"
                        v-model="hotel.addOnFeatures"
                        :options="addOnLicenses"
                        :disabled-options="disabledAddOnLicenses"
                        :label="$t('admin.pageHotel.addOnFeatures')"
                        :limit="10"
                        name="addOnFeatures"
                        label-expression="${option.name}" />
        <hm-multiselect v-if="!isEmpty(revocableLicenses)"
                        v-model="hotel.revokedFeatures"
                        :options="revocableLicenses"
                        :label="$t('admin.pageHotel.revokedFeatures')"
                        :limit="10"
                        name="revokedFeatures"
                        label-expression="${option.name}" />
        <hm-multiselect v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                        v-model="hotel.additionalBillingItems"
                        :options="additionalBillingItems"
                        :label="`${$t('admin.pageHotel.additionalBillingItems')} 🔒`"
                        :limit="10"
                        name="additionalBillingItems"
                        label-expression="${option.name}" />
        <hm-input-number v-model="hotel.licensedRooms"
                         :disabled="!hasRole('ROLE_SYSADMIN')"
                         :label="$t('FieldName.LicensedRooms')"
                         validate="min_value:1"
                         name="licensedRooms"/>
        <hm-input-toggle v-if="hasRole('ROLE_SYSADMIN') && hotel.deactivated === true" v-model="hotel.deactivated" :label="$t('admin.pageHotel.deactived')" cols="12 6@tablet 3@desktop" />
      </card>

      <hm-floating-buttons>
        <hm-floating-button v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                            label="Create Past Hotel Dates (2 Years) 🔒"
                            icon="cal"
                            color="blue"
                            @click="createPastHotelDates" />
        <hm-floating-button v-if="hotel.deactivated !== true && hasRole('ROLE_SYSADMIN')"
                            label="Deactivate Hotel 🔒"
                            icon="delete"
                            color="red"
                            @click="deactivateHotel(false)" />
        <hm-floating-button v-if="hotel.deactivated && hasRole('ROLE_SYSADMIN')"
                            label="Reactivate Hotel 🔒"
                            icon="save"
                            color="green"
                            @click="reactivateHotel" />
      </hm-floating-buttons>
    </template>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import vue2Dropzone from 'vue2-dropzone';
import draggable from 'vuedraggable';
import { cloneDeep, difference, endsWith, groupBy, isEmpty, isNil, sortBy, trim, uniq, uniqBy } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import HmCardInner from '../../lib/layout/HmCardInner';
import HmInputNumber from '../../lib/inputs/HmInputNumber';
import striptags from 'striptags';
import HmInputToggle from '../../lib/inputs/HmInputToggle';
import { TimelineMax, TweenMax } from 'gsap';
import HmCol from '../../lib/layout/HmCol.vue';

export default {
  name: 'PageHotel',
  components: { HmCol, HmInputToggle, HmInputNumber, HmCardInner },
  mixins: [HotelmeisterMixin, SaveWatchMixin],

  autosave: [
    {
      name: 'hotel',
      variable: 'hotel',
      event: 'hotelSettings.update',
      exclude: ['partnerAdmins'],
      fn: function (cbSaveComplete) {
        this.axios.put(this.serviceUrls.hotelDetails.base(this), this.hotel)
          .then((response) => {
            this.showMessages(response);
            this.hotel.emailSenderVerified = response.data.data.emailSenderVerified;
            cbSaveComplete();
          })
          .catch((error) => {
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  data() {
    return {
      hotel: null,
    };
  },

  computed: {
    additionalLanguages: function () {
      return isNil(this.hotel) ? [] : this.getI18nLanguages(this.$shortLocale).filter(it => it.id !== this.hotel.primaryLanguage);
    },
    fallbackLanguages: function () {
      const additionalLanguageKeys = isNil(this.hotel) ? [] : this.hotel.additionalLanguages;
      return isNil(this.hotel) ? [] : this.getI18nLanguages(this.$shortLocale).filter(it => it.id === this.hotel.primaryLanguage || additionalLanguageKeys.includes(it.id));
    },
    featureBundlesOptions: function () {
      return this.featureBundles
        .map(it => ({
          id: it,
          name: it,
        }));
    },
    featureBundles: function () {
      if (isNil(this.hotelSettings) || isNil(this.hotelSettings.hotel) || isNil(this.hotel)) return [];
      if (isEmpty(this.enums)) return [];
      const ret = this.enums.FeatureBundle
        .filter((it) => {
          const allowedCountries = this.enums.ADDITIONAL_DATA.FeatureBundle[it].allowedCountries;
          if (isEmpty(allowedCountries)) return true;
          else if (allowedCountries.includes(this.hotel.countryCode.toUpperCase())) return true;
          else return false;
        });

      if (this.hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')) return ret;
      else {
        return ret
          // remove all bundles which are limited to other white label partners
          .filter((it) => {
            if (this.hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')) return true;
            const whiteLabelPartners = this.enums.ADDITIONAL_DATA.FeatureBundle[it].whiteLabelPartners;
            if (isEmpty(whiteLabelPartners)) return true;
            if (whiteLabelPartners.includes(this.hotelSettings.hotel.whiteLabel)) return true;
            return false;
          })
          // remove all bundles which are not selected and do not start with plan_
          .filter((it) => {
            const whiteLabelPartners = this.enums.ADDITIONAL_DATA.FeatureBundle[it].whiteLabelPartners;
            if (this.hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')) return true;
            if (isNil(this.hotel) || isEmpty(this.hotel.featureBundles)) return false;
            if (this.hotel.featureBundles.includes(it)) return true;
            if (this.hotelSettings.hotel.whiteLabel === 'Hotelmeister') return true;
            if (isEmpty(this.hotelSettings.hotel.whiteLabel)) return true;
            if (whiteLabelPartners.includes(this.hotelSettings.hotel.whiteLabel)) return true;
            if (!['Rainmaker', 'AllInOne'].includes(this.hotelSettings.hotel.whiteLabel) && it.toLowerCase().indexOf('plan_') === 0) return true;
            if (isEmpty(whiteLabelPartners)) return false;
            return false;
          });
      }
    },
    disabledFeatureBundles: function () {
      if (this.hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')) return [];
      else return this.featureBundles.filter(it => ['Fiscalization_DE', 'WorkInProgress'].includes(it));
    },
    disabledAddOnLicenses: function () {
      if (this.hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')) return [];
      else return this.addOnLicenses.filter(it => ['CashBook'].includes(it));
    },
    revocableLicenses: function () {
      if (isEmpty(this.hotel.featureBundles)) return [];

      return sortBy(uniqBy(this.hotel.featureBundles
        .map(it => this.enums.ADDITIONAL_DATA.FeatureBundle[it])
        .flatMap(it => difference(it.licenses, it.nonRevocableLicenses))
        .map(it => ({
          id: it,
          name: it,
        })), 'id'), 'name');
    },
    additionalBillingItems: function () {
      return this.enums.AdditionalBillingItems.map(it => ({
        id: it,
        name: it,
      }));
    },
    addOnLicenses: function () {
      if (isEmpty(this.hotel.featureBundles)) return [];

      return sortBy(
        uniqBy(
          this.hotel.featureBundles
            .map(it => this.enums.ADDITIONAL_DATA.FeatureBundle[it])
            .flatMap(it => it.addOnLicenses)
            .filter((it) => {
              switch (it) {
                // case 'OnlineBookingAdditionalServices': return this.hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN');
                case 'InterfaceCmConnectCX': return !['rainmaker'].includes(this.hotelSettings.hotel.whiteLabel);
                default: return true;
              }
            })
            .map(it => ({
              id: it,
              name: it,
            })),
          'id',
        ),
        'name',
      );
    },
  },

  watch: {
    'hotel.featureBundles': {
      handler: function (newVal, oldVal) {
        if (isNil(oldVal)) return;
        if (isNil(newVal)) return;
        const disabledFeatureBundles = this.disabledFeatureBundles;

        const notSelectedDisabledOptions = disabledFeatureBundles.filter(it => !oldVal.includes(it));
        const ret = newVal.filter(it => !notSelectedDisabledOptions.includes(it));

        if (ret.length !== newVal.length) this.hotel.featureBundles = ret;
      },
      deep: true,
    },
  },

  created: function () {
    this.loadData();
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    isEmpty,
    striptags(content) {
      return striptags(content);
    },
    loadData() {
      this.axios.get(this.serviceUrls.hotelDetails.base(this))
        .then((response) => {
          this.hotel = response.data.data;
        });
    },
    zonesForCountry(country) {
      return this.$moment.tz.zonesForCountry(country);
    },
    deactivateHotel(confirmedManualDeactivation = false) {
      const featuresRequirementManualDeactivation = [];
      if (this.hasLicense('InterfaceCharPmsLink')) {
        featuresRequirementManualDeactivation.push('InterfaceCharPmsLink');
        if (!this.hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')) {
          this.showErrorMessage('Please contact support to terminate the Char PMS Link contract, before deactivating the hotel. Failure to do so will result in additional costs.');
          return;
        }
      }
      if (!isEmpty(featuresRequirementManualDeactivation) && !confirmedManualDeactivation) {
        this.showConfirmModal(
          `The following features require manual deactivation: <pre>${featuresRequirementManualDeactivation.join(', ')}</pre>Make sure to disable them and <b>manually cancel contracts</b> where required, before proceeding.</i>`,
          () => {
            this.axios.post(this.serviceUrls.hotelDetails.deactivate(this))
              .then((response) => {
                this.deactivateHotel(true);
              })
              .catch((error) => {
                this.showMessages(error.response);
              });
          },
        );
      } else if (isEmpty(featuresRequirementManualDeactivation) || confirmedManualDeactivation) {
        this.showConfirmModal(
          `Are you sure you would like to <b><u>permanently disable</u></b> the hotel <b>${this.hotel.name}</b>?<br/><br/><i>This will disable ALL interfaces, ALL cash desks, ALL users, ...</i>`,
          () => {
            this.axios.post(this.serviceUrls.hotelDetails.deactivate(this))
              .then((response) => {
                this.hotel.deactivated = true;
                this.showSuccessMessage();
                this.$serviceBus.$emit('hotelSettings.update.immediate');
              })
              .catch((error) => {
                this.showMessages(error.response);
              });
          },
        );
      }
    },
    reactivateHotel() {
      this.showConfirmModal(
        `Are you sure you would like to <b><u>reactivate</u></b> the hotel <b>${this.hotel.name}</b>?`,
        () => {
          this.axios.post(this.serviceUrls.hotelDetails.reactivate(this))
            .then((response) => {
              this.hotel.deactivated = false;
              this.showSuccessMessage();
              this.$serviceBus.$emit('hotelSettings.update.immediate');
            })
            .catch((error) => {
              this.showMessages(error.response);
            });
        },
      );
    },
    verifyEmailSender(emailAddress) {
      if (this.hotel.emailSenderVerified === true) return;
      setTimeout(() => {
        this.axios.post(this.serviceUrls.hotelDetails.verifyEmailSender(this))
          .then((response) => {
            this.showSuccessMessage();
          })
          .catch((error) => {
            this.showMessages(error.response);
          });
      }, 1000); // wait a little bit to make sure the changes have been saved
    },
    verifyEmailFromDomain() {
      if (this.hotel.additionalFields.AWS_SES_MAIL_FROM_DOMAIN_VERIFIED === true) return;
      setTimeout(() => {
        this.axios.post(this.serviceUrls.hotelDetails.verifyEmailFromDomain(this))
          .then((response) => {
            this.showSuccessMessage();
            this.loadData();
          })
          .catch((error) => {
            this.showMessages(error.response);
          });
      }, 1000); // wait a little bit to make sure the changes have been saved
    },
    removeEmailFromDomain() {
      if (this.hotel.additionalFields.AWS_SES_MAIL_FROM_DOMAIN_VERIFIED === true) return;
      setTimeout(() => {
        this.axios.post(this.serviceUrls.hotelDetails.removeEmailFromDomain(this))
          .then((response) => {
            this.showSuccessMessage();
            this.loadData();
          })
          .catch((error) => {
            this.showMessages(error.response);
          });
      }, 1000); // wait a little bit to make sure the changes have been saved
    },
    verifyEmailDomain(emailDomain) {
      if (this.hotel.additionalFields[`AWS_SES_DOMAIN_VERIFIED_${emailDomain.toUpperCase()}`] === true && !this.hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')) return;
      setTimeout(() => {
        this.axios.post(this.serviceUrls.hotelDetails.verifyEmailDomain(this))
          .then((response) => {
            this.showSuccessMessage();
            this.loadData();
          })
          .catch((error) => {
            this.showMessages(error.response);
          });
      }, 1000); // wait a little bit to make sure the changes have been saved
    },
    copyDkimData() {
      const trimText = function (text) {
        return text.split(/[\r\n\t]+/)
          .map(it => trim(it))
          .filter(it => !isEmpty(it))
          .join('\r\n');
      };

      const copy = [
        `DNS Entries (${this.hotel.additionalFields.AWS_SES_DOMAIN})`,
        trimText(this.$refs.dkimData.$el.querySelector('p').innerText),
        trimText(this.$refs.spf.$el.innerText),
        trimText(this.$refs.dmarc.$el.innerText),
        isNil(this.$refs.mailFromDomainData) ? '' : `Additional DNS Entries (${this.hotel.additionalFields.AWS_SES_DOMAIN})`,
        isNil(this.$refs.mailFromDomainData) ? '' : trimText(this.$refs.mailFromDomainData.$el.querySelector('p').innerText),
      ].join('\n\n');

      this.$copy(copy);
      this.animateCopyClick(this.$refs.dkimData.$el);
      this.animateCopyClick(this.$refs.spf.$el);
      this.animateCopyClick(this.$refs.dmarc.$el);
      if (!isNil(this.$refs.mailFromDomainData)) this.animateCopyClick(this.$refs.mailFromDomainData.$el);
    },
    animateCopyClick(el) {
      const tl = new TimelineMax({
        onComplete: () => {
          el.style.boxShadow = null;
        },
      });
      tl.add(TweenMax.fromTo(el, 0.25, { backgroundColor: '#fff' }, { backgroundColor: '#bbdcd8' }));
      tl.add(TweenMax.fromTo(el, 0.5, { backgroundColor: '#bbdcd8' }, { backgroundColor: '#fff' }));
    },
    onPartnerAdminStatusChange(partnerTenantId, $event) {
      if ($event) {
        this.axios.post(this.serviceUrls.hotelDetails.addPartnerAdmin(this), partnerTenantId, {
          headers: {
            'Content-type': 'application/json',
          },
        })
          .then((_) => {
            this.showSuccessMessage();
          })
          .catch((_) => {
            this.showErrorMessage();
          });
      } else {
        this.axios.delete(this.serviceUrls.hotelDetails.removePartnerAdmin(this, partnerTenantId))
          .then((_) => {
            this.showSuccessMessage();
          })
          .catch((_) => {
            this.showErrorMessage();
          });
      }
    },
    createPastHotelDates() {
      this.axios.post(this.serviceUrls.hotelDetails.createPastHotelDates(this), null, {
        headers: {
          'Content-type': 'application/json',
        },
      })
        .then((_) => {
          this.showSuccessMessage();
        })
        .catch((error) => {
          this.showMessages(error.response);
        });
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
  @import '~styles/import';

  .hm-input {
    &__image {
      &-wrapper {
        max-height: 15.5rem;
        width:100%;
        padding: 1rem;
        border-radius:0.3rem;
        border: 1px solid $middlegray;
        margin-bottom: 1rem;
        display: flex;
        flex-direction: row;
        position: relative;
        flex: 1;
        justify-content: center;
      }
      object-fit: scale-down;
      flex: 0 0 auto;
    }
  }
</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/PageHotel.vue